import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { nextAt, nextMonday } from '@/lib/timePresets';

export interface TimeMenuProps {
  /** The heading above the presets — what the chosen time is *for*. */
  title: string;
  presets: { label: string; at: Date }[];
  onPick: (at: Date) => void;
  onClose: () => void;
  /** Distinguishes the two datetime inputs when both menus exist on a page. */
  inputId: string;
  className?: string;
}

/**
 * Pick a future time, from presets or a picker.
 *
 * The presets are the times people actually mean — "tomorrow morning", "after
 * the weekend" — because a bare date picker turns a two-second decision into a
 * calendar exercise. The picker is still there for the rest.
 *
 * Shared between sending later and being reminded: they are the same question
 * asked twice, and reminders used to ask it through `window.prompt`.
 */
export function TimeMenu({
  title,
  presets,
  onPick,
  onClose,
  inputId,
  className,
}: TimeMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [custom, setCustom] = useState('');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    const timer = window.setTimeout(
      () => window.addEventListener('mousedown', onPointerDown),
      0,
    );
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={title}
      className={cn(
        'w-64 rounded-xl bg-surface-2 p-1.5 shadow-overlay ring-1 ring-surface-3',
        className,
      )}
    >
      <p className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-500">{title}</p>
      {presets.map((preset) => (
        <button
          key={preset.label}
          type="button"
          onClick={() => onPick(preset.at)}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-200 transition-colors hover:bg-surface-3"
        >
          <span className="flex-1">{preset.label}</span>
          <span className="text-[11px] text-slate-500">{format(preset.at, 'EEE HH:mm')}</span>
        </button>
      ))}

      <div className="mt-1 border-t border-surface-3 p-1.5">
        <label className="block text-[11px] text-slate-500" htmlFor={inputId}>
          Pick a time
        </label>
        <div className="mt-1 flex gap-1.5">
          <input
            id={inputId}
            type="datetime-local"
            value={custom}
            onChange={(event) => setCustom(event.target.value)}
            className="min-w-0 flex-1 rounded-lg bg-surface-3 px-2 py-1 text-xs text-slate-100 outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="button"
            disabled={!custom}
            onClick={() => {
              const at = new Date(custom);
              if (!Number.isNaN(at.getTime())) onPick(at);
            }}
            className="rounded-lg bg-brand-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-40"
          >
            Set
          </button>
        </div>
      </div>
    </div>
  );
}

/** When a reminder about a message should surface. */
export function RemindMenu({
  onRemindAt,
  onClose,
  className,
}: {
  onRemindAt: (remindAt: Date) => void;
  onClose: () => void;
  className?: string;
}) {
  return (
    <TimeMenu
      title="Remind me"
      inputId="remind-custom"
      className={className}
      onClose={onClose}
      onPick={onRemindAt}
      presets={[
        { label: 'In 20 minutes', at: new Date(Date.now() + 20 * 60_000) },
        { label: 'In an hour', at: new Date(Date.now() + 60 * 60_000) },
        { label: 'Tomorrow, 9am', at: nextAt(9, 1) },
        { label: 'Monday, 9am', at: nextMonday(9) },
      ]}
    />
  );
}
